"use client";

import { Close24xLarge } from "@/components/icons";
import { FirstWordBold } from "@/components/ui/FirstWordBold";
import {
	ModalClose,
	ModalContent,
	ModalHeader,
	ModalRoot,
	ModalViewport,
} from "@/components/ui/modal";
import { ScrollAreaBar, ScrollAreaRoot } from "@/components/ui/ScrollArea";
import { TabBar, type Tab } from "@/components/ui/TabBar";
import { Text } from "@/components/ui/Text";
import { useModal } from "@/hooks/useModal";
import type { ProductSizes } from "@/lib/centra/formatters/sizes";
import { clsx } from "@frend-digital/ui";
import { useQueryParams } from "@frend-digital/ui/next";
import { ScrollAreaViewport } from "@radix-ui/react-scroll-area";
import { useTranslations } from "next-intl";
import {
	Suspense,
	useEffect,
	useRef,
	useState,
	type ComponentProps,
	type ReactNode,
} from "react";
import type { Measurements } from "../../ProductDetailsPage";
import { SelectedProductSize } from "../SelectedProductSize";
import { SizeSelector } from "../SizeSelector";
import styles from "./index.module.css";

export const UnsuspendedSizeModalTrigger = ({
	children,
	...rest
}: ComponentProps<"button">) => {
	const { open } = useModal("size");

	return (
		<button
			onClick={() => {
				open();
			}}
			{...rest}>
			{children}
		</button>
	);
};

export const SizeModalTrigger: typeof UnsuspendedSizeModalTrigger = (props) => {
	return (
		<Suspense fallback={props.children}>
			<UnsuspendedSizeModalTrigger {...props} />
		</Suspense>
	);
};

export type SizeModalProps = Partial<{
	children: ReactNode;
	sizes?: ProductSizes;
	measurements?: Measurements;
	sizeChartName?: string;
}>;

const formatContent = (content: string, inches?: boolean) => {
	const convertToInches = (value: number) =>
		inches ? (value * 0.393700787).toFixed(1) : value;

	if (!content.includes("-")) {
		return inches ? convertToInches(Number(content)) : Number(content);
	}

	const [start, end] = content.split("-").map((c) => convertToInches(Number(c)));
	return `${start}-${end}`;
};

const createTabs = (defaultMeasurement?: string): Tab<string>[] => {
	if (!defaultMeasurement) return [];

	const alternativeMeasurement = defaultMeasurement === "cm" ? "in" : "cm";

	return [
		{
			name: <Text variant="body2">{defaultMeasurement.toUpperCase()}</Text>,
			value: defaultMeasurement,
		},
		{
			name: <Text variant="body2">{alternativeMeasurement.toUpperCase()}</Text>,
			value: alternativeMeasurement,
		},
	];
};

export const UnsuspendedSizeModal = ({
	measurements,
	sizes,
	sizeChartName,
}: SizeModalProps) => {
	const tabs = createTabs(measurements?.unit);

	const [activeTab, setActiveTab] = useState<Tab<string> | undefined>(
		tabs.find((tab) => tab.value === measurements?.unit),
	);

	const { isOpen, setOpen } = useModal("size");
	const searchParams = useQueryParams();
	const selectedSize = searchParams.get("size");
	const t = useTranslations("product.detail");

	const getContent = (x: number, y: number, inches = false) => {
		const cell = measurements?.contents?.find((item) => item.x === x && item.y === y);
		const content = cell?.content;

		return content ? formatContent(content, inches) : undefined;
	};

	const isOutOfStock = (size: string) => sizes?.find((s) => s.name === size && !s.stock);

	const tableColumnRefs = useRef<(HTMLTableCellElement | null)[]>([]);

	useEffect(() => {
		if (!selectedSize) return;

		const selectedIndex = measurements?.x?.indexOf(selectedSize);
		if (selectedIndex !== -1 && tableColumnRefs.current[selectedIndex!]) {
			tableColumnRefs.current[selectedIndex!]?.scrollIntoView({
				behavior: "smooth",
				block: "center",
				inline: "center",
			});
		}
	}, [selectedSize, measurements?.x]);

	return (
		<ModalRoot open={isOpen} onOpenChange={setOpen}>
			<ModalViewport>
				<ModalContent>
					<article className={styles.productModalBody}>
						<ModalHeader className={styles.header}>
							<Text className={styles.modalHeader} asChild>
								<h2>{t("sizeGuide")}</h2>
							</Text>
							<ModalClose>
								<Close24xLarge
									className={styles.productModalCloseIcon}
									onClick={() => setOpen(!isOpen)}
								/>
							</ModalClose>
						</ModalHeader>
						<div className={styles.sizes}>
							<SelectedProductSize />
							{sizes && <SizeSelector sizes={sizes} />}
						</div>
						<div className={styles.measurements}>
							{sizeChartName && (
								<Text variant="body2">
									<FirstWordBold>{t("measurements")}</FirstWordBold>
								</Text>
							)}
							<TabBar
								className={styles.tabs}
								value={activeTab?.value}
								onValueChange={(val) => {
									const newVal = tabs.find((tab) => tab.value === val);
									if (newVal) {
										setActiveTab(newVal);
									}
								}}
								tabs={tabs}
							/>
						</div>
						<ScrollAreaRoot>
							<ScrollAreaViewport className={styles.tableWrapper}>
								<table className={styles.table}>
									<thead>
										<tr>
											<th className={clsx(styles.left, styles.text)}>
												<Text className={styles.text}>
													Measures: {activeTab?.value?.toUpperCase()}
												</Text>
											</th>
											{measurements?.x?.map((header, index) => {
												const outOfStock = isOutOfStock(header);
												return (
													<th
														key={index}
														ref={
															selectedSize === header
																? (el) => {
																		tableColumnRefs.current[index] = el;
																	}
																: null
														}
														className={clsx(
															selectedSize === header ? styles.selected : "",
															outOfStock && styles.disabled,
														)}>
														<Text className={styles.text}>{header}</Text>
													</th>
												);
											})}
										</tr>
									</thead>
									<tbody>
										{measurements?.y?.map((rowHeader, rowIndex) => (
											<tr key={rowIndex}>
												<th className={styles.left}>
													<Text className={styles.text}>{rowHeader}</Text>
												</th>
												{measurements?.x?.map((header, colIndex) => {
													const outOfStock = isOutOfStock(header);
													return (
														<td
															key={colIndex}
															className={clsx(
																selectedSize === header ? styles.selected : "",
																outOfStock && styles.disabled,
															)}>
															{outOfStock ? (
																<hr className={styles.hr} />
															) : (
																<Text className={styles.text}>
																	{getContent(
																		colIndex,
																		rowIndex,
																		activeTab?.value === "in",
																	)}
																	{activeTab?.value}
																</Text>
															)}
														</td>
													);
												})}
											</tr>
										))}
									</tbody>
								</table>
								<ScrollAreaBar className={styles.scrollBar} orientation="horizontal" />
							</ScrollAreaViewport>
						</ScrollAreaRoot>
					</article>
				</ModalContent>
			</ModalViewport>
		</ModalRoot>
	);
};

export const SizeModal: typeof UnsuspendedSizeModal = (props) => {
	return (
		<Suspense>
			<UnsuspendedSizeModal {...props} />
		</Suspense>
	);
};
